import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import { MessageSquare, RefreshCw, Bot, User, Users, Lock, Inbox } from 'lucide-react';
import { formatIST } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import api from '../lib/api';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip as ChartTooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

const BLUE = '#1d4ed8';
const SKY = '#0ea5e9';
const STATUS_COLORS: Record<string, string> = { open: SKY, resolved: BLUE, pending: '#38bdf8', snoozed: '#93c5fd', unknown: '#cbd5e1' };

interface BitespeedProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface LiveFeedItem {
  received_at: string; sender_type: 'Customer' | 'Agent' | 'Bot' | 'System'; sender_name: string;
  channel: string; content: string; content_type: string; private: boolean;
}
interface AssigneeRow { assignee: string; total: number; open: number; is_bot: boolean; }
interface BitespeedData {
  connected: boolean;
  synced_at: string | null;
  total_conversations: number;
  frt_bot_minutes: number | null; frt_bot_count: number;
  frt_human_minutes: number | null; frt_human_count: number;
  resolution_bot_minutes: number | null; resolution_bot_count: number;
  resolution_human_minutes: number | null; resolution_human_count: number;
  csat_avg: number | null; csat_count: number;
  daily_conversations: { date: string; count: number }[];
  live_feed: LiveFeedItem[];
  conversations_by_status: { status: string; count: number }[];
  assignee_breakdown: AssigneeRow[];
  bot_vs_human: { bot: number; human: number; unassigned: number };
}

const SENDER_STYLES: Record<string, { badge: string; icon: any }> = {
  Customer: { badge: 'bg-sky-50 text-sky-700 border-sky-100', icon: User },
  Agent: { badge: 'bg-blue-50 text-blue-700 border-blue-100', icon: Users },
  Bot: { badge: 'bg-amber-50 text-amber-700 border-amber-100', icon: Bot },
  System: { badge: 'bg-slate-50 text-slate-500 border-slate-100', icon: MessageSquare },
};

const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const mins = (v: number | null) => (v === null || v === undefined ? '—' : v < 1 ? '<1 min' : `${v.toFixed(1)} min`);

function KpiCard({ label, value, note, accent, testId }: { label: string; value: string; note: string; accent: string; testId: string }) {
  return (
    <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm space-y-2" data-testid={testId}>
      <div className="h-1.5 w-8 rounded-full" style={{ background: accent }} />
      <p className="text-2xl font-bold text-slate-800 font-mono tracking-tight">{value}</p>
      <p className="text-[10.5px] font-semibold text-slate-500">{label}</p>
      <p className="text-[9px] text-slate-400 font-mono">{note}</p>
    </div>
  );
}

export default function Bitespeed({ onHover }: BitespeedProps) {
  const [data, setData] = useState<BitespeedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  const fetchData = () => {
    setLoading(true);
    // Hits our own MongoDB `bitespeed_events` collection directly — BiteSpeed pushes events to us
    // via webhook in real time, so there is no external API call here, only a re-aggregation of
    // already-stored events for the picked window.
    api.get('/cx/bitespeed', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [range]);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await api.post('/cx/bitespeed/sync');
      fetchData();
    } catch (e) {
      // connection badge staying stale surfaces the failure
    } finally {
      setSyncing(false);
    }
  };

  const frtChartData = data ? [
    { name: 'Bot', minutes: data.frt_bot_minutes || 0, count: data.frt_bot_count },
    { name: 'Human', minutes: data.frt_human_minutes || 0, count: data.frt_human_count },
  ] : [];
  const resChartData = data ? [
    { name: 'Bot', minutes: data.resolution_bot_minutes || 0, count: data.resolution_bot_count },
    { name: 'Human', minutes: data.resolution_human_minutes || 0, count: data.resolution_human_count },
  ] : [];

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="bitespeed-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Conversational CRM & Support
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            BiteSpeed — CX Performance
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Total Conversations, Response &amp; Resolution Time (Bot vs. Human) and CSAT — computed live from BiteSpeed's webhook feed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="bitespeed" />
          <button
            data-testid="bitespeed-manual-sync-button"
            onClick={handleManualSync}
            disabled={syncing}
            className="px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-semibold border bg-white text-blue-700 border-blue-100 hover:border-blue-300 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing…' : 'Manual Sync'}</span>
          </button>
          <div
            data-testid="bitespeed-connection-badge"
            className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
              data?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'
            }`}
          >
            <MessageSquare className="h-4.5 w-4.5" />
            <span>{data?.connected ? `Synced ${data.synced_at ? formatIST(data.synced_at) : ''}` : 'No Events Yet'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="bitespeed-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
          Loading live BiteSpeed data…
        </div>
      ) : !data?.connected ? (
        <div data-testid="bitespeed-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm flex items-center gap-3 text-xs text-slate-500">
          <Inbox className="h-4 w-4 text-slate-400 shrink-0" />
          <span>Waiting for the first BiteSpeed event. Once a conversation, message, or contact is created in BiteSpeed, it will appear here automatically.</span>
        </div>
      ) : (
        <>
          {/* KPI Cards — the 4 requested metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total Conversions" testId="bitespeed-kpi-total-conversations" accent={BLUE}
              value={String(data.total_conversations)}
              note="Conversations started · selected window"
            />
            <KpiCard
              label="Avg First Response Time" testId="bitespeed-kpi-frt" accent={SKY}
              value={mins(data.frt_bot_minutes ?? data.frt_human_minutes)}
              note={`Bot ${mins(data.frt_bot_minutes)} (${data.frt_bot_count}) · Human ${mins(data.frt_human_minutes)} (${data.frt_human_count})`}
            />
            <KpiCard
              label="Avg Resolution Time" testId="bitespeed-kpi-resolution" accent="#0369a1"
              value={mins(data.resolution_bot_minutes ?? data.resolution_human_minutes)}
              note={`Bot ${mins(data.resolution_bot_minutes)} (${data.resolution_bot_count}) · Human ${mins(data.resolution_human_minutes)} (${data.resolution_human_count})`}
            />
            <KpiCard
              label="CSAT Score" testId="bitespeed-kpi-csat" accent="#64748b"
              value={data.csat_avg !== null ? `${data.csat_avg} / 5` : 'No surveys yet'}
              note={data.csat_count ? `${data.csat_count} survey responses · selected window` : 'BiteSpeed CSAT surveys not received yet'}
            />
          </div>

          {/* FRT / Resolution — Bot vs Human comparison charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="bitespeed-frt-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">First Response Time — Bot vs Human (minutes)</span>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={frtChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any, _n: any, p: any) => [`${Number(v).toFixed(1)} min avg (${p.payload.count} convos)`, 'Avg FRT']} />
                    <Bar dataKey="minutes" radius={[6, 6, 0, 0]} name="Avg FRT (min)">
                      <Cell fill="#f59e0b" /><Cell fill={BLUE} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {data.frt_bot_count + data.frt_human_count === 0 && <p className="text-xs text-slate-400 font-mono">No first responses recorded in this window.</p>}
            </div>
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="bitespeed-resolution-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Resolution Time — Bot vs Human (minutes)</span>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={resChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any, _n: any, p: any) => [`${Number(v).toFixed(1)} min avg (${p.payload.count} convos)`, 'Avg Resolution']} />
                    <Bar dataKey="minutes" radius={[6, 6, 0, 0]} name="Avg Resolution (min)">
                      <Cell fill="#f59e0b" /><Cell fill={BLUE} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {data.resolution_bot_count + data.resolution_human_count === 0 && <p className="text-xs text-slate-400 font-mono">No resolved conversations in this window.</p>}
            </div>
          </div>

          {/* Volume trend + Status breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="bitespeed-volume-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Daily Conversation Volume — Selected Window</span>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily_conversations} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} tickFormatter={(d) => { try { return format(new Date(d + 'T00:00:00'), 'dd MMM'); } catch { return d; } }} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} allowDecimals={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} labelFormatter={(d) => { try { return format(new Date(d + 'T00:00:00'), 'dd MMM yyyy'); } catch { return d; } }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Conversations" fill={SKY} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {data.daily_conversations.length === 0 && <p className="text-xs text-slate-400 font-mono">No conversations started in this window.</p>}
            </div>
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="bitespeed-status-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Conversations by Status</span>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.conversations_by_status} dataKey="count" nameKey="status" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {data.conversations_by_status.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || '#cbd5e1'} />)}
                    </Pie>
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any, n: any) => [`${v} conversations`, n]} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11, textTransform: 'capitalize' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {data.conversations_by_status.length === 0 && <p className="text-xs text-slate-400 font-mono">No conversations in this window.</p>}
            </div>
          </div>

          {/* Live Feed */}
          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="bitespeed-live-feed">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Live Feed — Interaction Timeline (Selected Window)</span>
              <span className="text-[9px] font-mono bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full border border-blue-100 uppercase font-bold">
                {data.live_feed.length} shown
              </span>
            </div>
            {data.live_feed.length === 0 ? (
              <p className="text-xs text-slate-400 py-6 text-center">No message-level events in this window.</p>
            ) : (
              <div className="overflow-y-auto max-h-[420px] space-y-2.5">
                {data.live_feed.map((f, i) => {
                  const style = SENDER_STYLES[f.sender_type] || SENDER_STYLES.System;
                  const Icon = style.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-2xl border border-slate-100 hover:border-blue-200 transition-colors">
                      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border ${style.badge}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${style.badge}`}>{f.sender_type}</span>
                          <span className="text-xs font-semibold text-slate-700">{f.sender_name}</span>
                          <span className="text-[10px] font-mono text-slate-400">via {f.channel}</span>
                          {f.private && <span className="flex items-center gap-1 text-[10px] text-slate-400"><Lock className="h-2.5 w-2.5" />Internal note</span>}
                          <span className="text-[10px] font-mono text-slate-400 ml-auto whitespace-nowrap">{formatIST(f.received_at)}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2 font-sans">{f.content || <span className="italic text-slate-400">No text content</span>}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Active Tickets per Agent */}
          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="bitespeed-assignee-chart">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Active Tickets per Agent — Selected Window</span>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.assignee_breakdown} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="assignee" stroke="#64748b" fontSize={10} tickLine={false} width={110} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any) => [`${v} open`, 'Open Tickets']} />
                  <Bar dataKey="open" name="Open Tickets" radius={[0, 6, 6, 0]}>
                    {data.assignee_breakdown.map((a, i) => <Cell key={i} fill={a.is_bot ? '#f59e0b' : BLUE} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {data.assignee_breakdown.length === 0 && <p className="text-xs text-slate-400 font-mono">No assigned conversations in this window.</p>}
          </div>
        </>
      )}
    </div>
  );
}
